import * as React from 'react';
import cn from 'classnames';
import { isValid, parse } from 'date-fns';
import DatePicker, {
  ReactDatePickerCustomHeaderProps,
  ReactDatePickerProps
} from 'react-datepicker';
import MediaQuery from 'react-responsive';

import 'react-datepicker/dist/react-datepicker.css';

import { BottomSheet } from '@nw/omega-design-system';
import { getDataMaskAttribute } from '@nw/shared/utils';

import { useResize } from '../../hooks/useResize';

import { DESKTOP_MIN_WIDTH, MOBILE_MAX_WIDTH } from './constants';
import { CustomButton } from './CustomButton';
import { CustomHeader } from './CustomHeader';
import {
  getDayClassName,
  getFormat,
  getPopperClassName,
  getSelectedFilterLabel,
  getTimeClassName,
  getWeekdayClassName,
  isMobile,
  safeDateFormat,
  safeDateRangeFormat
} from './utils';

import './styles.css';

export interface FilterType {
  label: string;
  value: string;
}

export interface DatePickerProps
  extends Omit<
    ReactDatePickerProps,
    | 'showTimeInput'
    | 'showTimeSelectOnly'
    | 'timeFormat'
    | 'withPortal'
    | 'useWeekdaysShort'
  > {
  inputProps?: any;
  filters?: Array<FilterType>;
  onClickFilter?: (value: string) => void;
  selectedFilterValue?: string;
  headerDisplayFormat?: string;
  placeholder?: string;
  error?: string;
  value?: string;
  disabled?: boolean;
  enableDataMask?: boolean;
}

export const OmegaDatePicker = (props: DatePickerProps) => {
  const {
    onChange,
    selected,
    onCalendarOpen,
    dateFormat,
    showTimeSelect,
    popperClassName,
    weekDayClassName,
    dayClassName,
    timeClassName,
    selectsRange,
    startDate,
    endDate,
    inputProps,
    filters,
    onClickFilter,
    headerDisplayFormat,
    selectedFilterValue,
    locale,
    shouldCloseOnSelect = false,
    placeholder,
    error,
    value,
    disabled,
    enableDataMask,
    ...rest
  } = props;

  const deviceWidth = useResize();

  const datePickerRef = React.useRef<DatePicker>() as React.RefObject<
    DatePicker<never>
  >;

  const requestCalendarClose = () => {
    datePickerRef.current?.setOpen(false);
  };

  const onChangeDate = (date, event) => {
    if (selectedFilterValue) {
      onClickFilter && onClickFilter('');
    }
    onChange(date, event);
  };

  const CalendarContainer = (props: { children: React.ReactNode[] }) => {
    return (
      <>
        <MediaQuery maxWidth={MOBILE_MAX_WIDTH}>
          <BottomSheet
            isOpen={datePickerRef.current?.isCalendarOpen()}
            containerClassName={cn('bottom-sheet-container')}
            overlayClassName={cn('bg-blue-gray-600')}
            hideCloseIcon={true}
            onRequestClose={requestCalendarClose}
          >
            {props.children}
          </BottomSheet>
        </MediaQuery>
        <MediaQuery minWidth={DESKTOP_MIN_WIDTH}>
          <div className={cn('bg-base-white md:flex')}>{props.children}</div>
        </MediaQuery>
      </>
    );
  };

  const onClickFilterItem = (value: string) => {
    if (onClickFilter) {
      onClickFilter(value);
      if (shouldCloseOnSelect) {
        datePickerRef.current?.setOpen(false);
      }
      if (selected) {
        onChange(null, undefined);
      }
    }
  };

  const renderCustomHeader = (params: ReactDatePickerCustomHeaderProps) => (
    <CustomHeader
      params={params}
      filters={filters}
      onClickFilter={onClickFilterItem}
      headerDisplayFormat={headerDisplayFormat}
      selectedFilterValue={selectedFilterValue}
    />
  );

  const getInputValue = () => {
    if (selectedFilterValue) {
      return getSelectedFilterLabel(filters, selectedFilterValue);
    } else if (selectsRange) {
      return safeDateRangeFormat(startDate ?? '', endDate ?? '', {
        dateFormat: getFormat(dateFormat, showTimeSelect),
        locale: locale
      });
    } else if (selected) {
      return safeDateFormat(selected, {
        dateFormat: getFormat(dateFormat, showTimeSelect),
        locale: locale
      });
    } else if (value) {
      let parsedDate;

      if (dateFormat) {
        parsedDate = parse(value, dateFormat as string, new Date());
      } else {
        parsedDate = new Date(value);
      }

      if (!isValid(parsedDate)) {
        parsedDate = new Date(value);
      }
      return safeDateFormat(parsedDate, {
        dateFormat: getFormat(dateFormat, showTimeSelect),
        locale: locale
      });
    }
    return '';
  };

  return (
    <DatePicker
      value={getInputValue()}
      placeholderText={placeholder ? placeholder : 'Select Date'}
      ref={datePickerRef}
      selected={selected}
      startDate={startDate}
      endDate={endDate}
      useWeekdaysShort
      showTimeSelect={showTimeSelect}
      selectsRange={selectsRange}
      withPortal={isMobile(deviceWidth)}
      dateFormat={getFormat(dateFormat, showTimeSelect)}
      onChange={onChangeDate}
      customInput={
        <CustomButton
          value={getInputValue()}
          placeholder={placeholder ? placeholder : 'Select Date'}
          error={error}
          {...getDataMaskAttribute(enableDataMask)}
        />
      }
      calendarContainer={(props) => <CalendarContainer {...props} />}
      weekDayClassName={(date) =>
        getWeekdayClassName(date, showTimeSelect, weekDayClassName)
      }
      popperClassName={getPopperClassName(popperClassName)}
      dayClassName={(date) =>
        getDayClassName(
          date,
          selected,
          selectsRange,
          startDate,
          endDate,
          showTimeSelect,
          dayClassName
        )
      }
      timeClassName={(date) => getTimeClassName(date, timeClassName)}
      locale={locale}
      shouldCloseOnSelect={shouldCloseOnSelect}
      scrollableYearDropdown={true}
      dropdownMode="select"
      disabled={disabled}
      {...rest}
    />
  );
};
